import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export function setAccessToken(token: string) { accessToken = token; }
export function clearAccessToken() { accessToken = null; }

const api: AxiosInstance = axios.create({
  baseURL: (import.meta.env.VITE_API_URL || '') + '/api',
  withCredentials: true,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - inject auth token
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  config.headers['X-Trace-Id'] = crypto.randomUUID();
  return config;
});

// Response interceptor - auto-refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as typeof error.config & { _retry?: boolean };

    if (error.response?.status === 401 && !original?._retry) {
      if (!original) return Promise.reject(error);

      // Never try to refresh for auth endpoints themselves — would cause an infinite loop
      // (e.g. login returns 401 for wrong credentials, then refresh also returns 401)
      if (original.url?.includes('/auth/')) return Promise.reject(error);

      original._retry = true;

      if (!refreshPromise) {
        refreshPromise = api
          .post('/auth/refresh')
          .then((res) => {
            const token = res.data.data.accessToken;
            setAccessToken(token);
            return token;
          })
          .catch(() => {
            clearAccessToken();
            // Clear persisted auth so ProtectedRoute redirects to /login instead of looping
            localStorage.removeItem('orbayx-auth');
            window.location.href = '/login';
            return '';
          })
          .finally(() => { refreshPromise = null; });
      }

      const newToken = await refreshPromise;
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
    }

    // Show error toast for 5xx
    if (error.response?.status && error.response.status >= 500) {
      toast.error('Server error. Please try again.');
    }

    return Promise.reject(error);
  },
);

export default api;
